/**
 * Referral Code Management Utility
 *
 * Handles capturing, storing, and retrieving referral codes from URLs
 * Supports persistence across navigation and page refreshes
 *
 * SECURITY FEATURES:
 * - Strict validation (alphanumeric + underscore/hyphen only)
 * - Sanitization to prevent XSS attacks
 * - No HTML/JavaScript special characters allowed
 * - Length limits (3-50 characters)
 * - Reserved route protection
 * - Validation on both storage and retrieval
 */

const REFERRAL_CODE_KEY = "betyakata_referral_code";
const REFERRAL_CODE_TIMESTAMP_KEY = "betyakata_referral_timestamp";
const REFERRAL_CODE_EXPIRY_DAYS = 30; // Referral code expires after 30 days

/**
 * Sanitizes a string to prevent XSS attacks
 * Removes any potentially dangerous characters
 * @param {string} str - The string to sanitize
 * @returns {string} - Sanitized string
 */
const sanitizeString = (str) => {
  if (!str || typeof str !== "string") return "";

  // Remove any HTML tags
  let sanitized = str.replace(/<[^>]*>/g, "");

  // Remove dangerous characters that could be used for XSS
  // Keep only alphanumeric, underscore, and hyphen
  sanitized = sanitized.replace(/[^a-zA-Z0-9_-]/g, "");

  // Trim whitespace
  sanitized = sanitized.trim();

  // Limit length
  sanitized = sanitized.substring(0, 50);

  return sanitized;
};

/**
 * Validates if a string is a valid referral code format
 * SECURITY: Strict validation to prevent XSS and injection attacks
 * Only allows: letters, numbers, underscores, hyphens (3-50 chars)
 * @param {string} code - The referral code to validate
 * @returns {boolean} - True if valid, false otherwise
 */
export const isValidReferralCode = (code) => {
  if (!code || typeof code !== "string") return false;

  // Type check
  if (typeof code !== "string") {
    console.warn("🔒 Security: Referral code must be a string");
    return false;
  }

  // Length check (prevent DoS attacks with huge strings)
  if (code.length < 3 || code.length > 50) {
    console.warn("🔒 Security: Referral code length must be 3-50 characters");
    return false;
  }

  // Check for dangerous characters that could indicate XSS attempt
  const dangerousChars = [
    "<",
    ">",
    '"',
    "'",
    "&",
    "(",
    ")",
    ";",
    "=",
    "%",
    "`",
    "$",
    "{",
    "}",
    "[",
    "]",
  ];
  if (dangerousChars.some((char) => code.includes(char))) {
    console.warn("🔒 Security: Referral code contains dangerous characters");
    return false;
  }

  // Check if code is not empty and doesn't match reserved routes
  const reservedRoutes = [
    "sports",
    "login",
    "register",
    "casinos",
    "promotions",
    "withdrawals",
    "deposits",
    "payment",
    "profile",
    "mybets",
    "transaction-history",
    "change-pin",
    "forgot-pin",
    "virtual-league",
    "jackpot",
    "games",
    "spribe",
    "aviator",
    "allsports",
    "betslip",
    "comming-soon",
    "bonuses-awarded",
    "refer-a-friend",
    "league-details",
    "fixture-details",
    "sports-details",
    "sport",
    "provider",
    "payment-success",
    "payment-error",
    "payment-verify",
    "jackpot-running",
    "jackpot-complete",
    "jackpot-upcoming",
    "contact-us",
    "faq",
    "faqs",
    "terms-and-conditions",
    "privacy-policy",
    "cookies-policy",
    "betting-rules",
    "responsible-gaming",
    "refund-policy",
    "promotion-details",
    "static",
    "icons-boxicons",
    "icons-fontawesome",
    "javascript",
    "script",
    "eval",
    "alert",
    "onerror",
    "onload",
    "onclick",
    "booming",
    "walletuu",
    "mobile-wallet",
    "slot",
    "delete-account",
    "hot-games",
    "bigwins",
    "countries",
    "all-leagues",
  ];

  const lowerCode = code.toLowerCase().trim();

  // Check if it's a reserved route or dangerous keyword
  if (reservedRoutes.includes(lowerCode)) {
    console.warn(
      "🔒 Security: Referral code matches reserved route or dangerous keyword",
    );
    return false;
  }

  // Check if it looks like a file or has special characters that shouldn't be in a referral code
  if (code.includes(".") || code.includes("/") || code.includes("\\")) {
    console.warn("🔒 Security: Referral code contains file path characters");
    return false;
  }

  // STRICT: Valid referral code - ONLY alphanumeric, underscores, hyphens (3-50 characters)
  // This regex prevents XSS, SQL injection, and other injection attacks
  const referralCodePattern = /^[a-zA-Z0-9_-]{3,50}$/;

  if (!referralCodePattern.test(code)) {
    console.warn("🔒 Security: Referral code failed pattern validation");
    return false;
  }

  return true;
};

/**
 * Checks if the stored referral code has expired
 * @returns {boolean} - True if expired, false otherwise
 */
const isReferralCodeExpired = () => {
  const timestamp = localStorage.getItem(REFERRAL_CODE_TIMESTAMP_KEY);
  if (!timestamp) return true;

  const storedDate = new Date(parseInt(timestamp));
  const now = new Date();
  const daysDifference = (now - storedDate) / (1000 * 60 * 60 * 24);

  return daysDifference > REFERRAL_CODE_EXPIRY_DAYS;
};

/**
 * Stores a referral code in localStorage with timestamp
 * IMPORTANT: Always overwrites/replaces any existing code with the new one
 * SECURITY: Sanitizes and validates before storage
 * @param {string} code - The referral code to store
 * @returns {boolean} - True if stored successfully, false otherwise
 */
export const storeReferralCode = (code) => {
  // First, sanitize the input
  const sanitizedCode = sanitizeString(code);

  if (!sanitizedCode) {
    console.warn("🔒 Security: Referral code is empty after sanitization");
    return false;
  }

  // Then validate the sanitized code
  if (!isValidReferralCode(sanitizedCode)) {
    console.warn(
      "🔒 Security: Invalid referral code format after sanitization:",
      sanitizedCode,
    );
    return false;
  }

  try {
    // Check if there's an existing code
    const existingCode = localStorage.getItem(REFERRAL_CODE_KEY);

    // If there's a different code, log the replacement (override behavior)
    if (existingCode && existingCode !== sanitizedCode) {
      console.info(
        "🔄 OVERRIDE: Replacing existing referral code:",
        existingCode,
        "with new code:",
        sanitizedCode,
      );
    }

    // Always store/overwrite - latest code wins
    // Store only the sanitized, validated code
    localStorage.setItem(REFERRAL_CODE_KEY, sanitizedCode);
    localStorage.setItem(REFERRAL_CODE_TIMESTAMP_KEY, Date.now().toString());

    console.info("✅ Referral code stored successfully:", sanitizedCode);
    return true;
  } catch (error) {
    console.error("🔒 Security: Error storing referral code:", error);
    return false;
  }
};

/**
 * Retrieves the stored referral code from localStorage
 * Returns null if expired or doesn't exist
 * SECURITY: Validates retrieved code before returning
 * @returns {string|null} - The referral code or null
 */
export const getReferralCode = () => {
  try {
    const code = localStorage.getItem(REFERRAL_CODE_KEY);

    if (!code) return null;

    // SECURITY: Validate the code even when retrieving from storage
    // This protects against tampering with localStorage
    if (!isValidReferralCode(code)) {
      console.warn(
        "🔒 Security: Stored referral code failed validation, clearing...",
      );
      clearReferralCode();
      return null;
    }

    // Check if expired
    if (isReferralCodeExpired()) {
      console.info("Referral code expired, clearing...");
      clearReferralCode();
      return null;
    }

    // Additional sanitization on retrieval as defense in depth
    const sanitizedCode = sanitizeString(code);

    // If sanitization changed the code, something is wrong
    if (sanitizedCode !== code) {
      console.warn(
        "🔒 Security: Referral code was modified during sanitization, clearing...",
      );
      clearReferralCode();
      return null;
    }

    return sanitizedCode;
  } catch (error) {
    console.error("🔒 Security: Error retrieving referral code:", error);
    return null;
  }
};

/**
 * Clears the referral code from localStorage
 * Should be called after successful registration
 */
export const clearReferralCode = () => {
  try {
    localStorage.removeItem(REFERRAL_CODE_KEY);
    localStorage.removeItem(REFERRAL_CODE_TIMESTAMP_KEY);
    console.info("Referral code cleared");
  } catch (error) {
    console.error("Error clearing referral code:", error);
  }
};

/**
 * Extracts referral code from URL pathname
 * Supports formats: {BASE_URL}/{referral_code}
 * @param {string} pathname - The URL pathname
 * @returns {string|null} - The referral code or null
 */
export const extractReferralCodeFromURL = (pathname) => {
  if (!pathname || pathname === "/") return null;

  // Remove leading/trailing slashes and get the first segment
  const segments = pathname
    .split("/")
    .filter((segment) => segment.trim() !== "");

  // If there are no segments or more than one segment, it's not a referral URL
  if (segments.length !== 1) return null;

  const potentialCode = segments[0];

  // Validate the code
  if (isValidReferralCode(potentialCode)) {
    return potentialCode;
  }

  return null;
};

/**
 * Checks if current URL contains a referral code and stores it
 * @param {string} pathname - The URL pathname to check
 * @returns {boolean} - True if referral code was captured, false otherwise
 */
export const captureReferralCodeFromURL = (pathname) => {
  const referralCode = extractReferralCodeFromURL(pathname);

  if (referralCode) {
    return storeReferralCode(referralCode);
  }

  return false;
};

/**
 * Gets referral code info for debugging
 * @returns {object} - Object with referral code details
 */
export const getReferralCodeInfo = () => {
  const code = localStorage.getItem(REFERRAL_CODE_KEY);
  const timestamp = localStorage.getItem(REFERRAL_CODE_TIMESTAMP_KEY);

  return {
    code,
    timestamp,
    isExpired: isReferralCodeExpired(),
    storedDate: timestamp ? new Date(parseInt(timestamp)).toISOString() : null,
  };
};
